import React, { useCallback } from "react";
import { AlertTriangle } from "lucide-react";

// ─── Inline error banner ─────────────────────────────────────────────────────

function ConversionError({ message, onAutoFix, autoFixLabel }) {
  if (!message) return null;
  return (
    <div
      className="mt-2 flex items-start gap-2 px-2.5 py-2 rounded-lg
                 bg-amber-50 dark:bg-amber-900/20
                 border border-amber-200 dark:border-amber-700/50"
    >
      <AlertTriangle size={12} className="text-amber-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-amber-800 dark:text-amber-300 leading-snug">{message}</p>
        {onAutoFix && (
          <button
            onClick={onAutoFix}
            className="mt-1 text-[10px] font-semibold text-amber-700 dark:text-amber-200
                       underline underline-offset-2 hover:text-amber-900
                       dark:hover:text-white transition-colors"
          >
            Switch back to {autoFixLabel} →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {Object}        props.chartData         raw chartData from backend
 * @param {Object}        props.editOverrides      current override state
 * @param {Function}      props.setEditOverrides   setter
 * @param {ChartValidator} props.validator         from useChartValidator(chartData)
 */
export default function ChartTypeSelector({
  chartData,
  editOverrides,
  setEditOverrides,
  validator,
}) {
  // ── Effective type currently shown in the dropdown ────────────────────────
  const currentRawType = editOverrides.chart_type || chartData.chart_type;
  const isArea =
    editOverrides.is_area !== null
      ? editOverrides.is_area
      : (chartData.meta?.is_area ?? false);
  const effectiveType = currentRawType === "line" && isArea ? "area" : currentRawType;

  // ── Build option list — bubble is backend-only, hide from UI ─────────────
  const dropdownOptions = validator
    .getValidOptions()
    .filter((o) => o.value !== "bubble");

  // ── Change handler ────────────────────────────────────────────────────────
  const handleChange = useCallback(
    (selectedValue) => {
      const targetType = selectedValue === "area" ? "line" : selectedValue;
      const result = validator.validate(targetType);

      setEditOverrides((p) => ({
        ...p,
        chart_type: targetType,
        is_area: selectedValue === "area",
        _conversionError: result.valid ? null : result.reason,
        _autoConvertTo:   result.valid ? null : result.autoConvertTo,
      }));
    },
    [validator, setEditOverrides]
  );

  // ── Auto-fix: revert to the suggested compatible type ─────────────────────
  const handleAutoFix = useCallback(() => {
    const fallback = editOverrides._autoConvertTo || chartData.chart_type || "bar";
    setEditOverrides((p) => ({
      ...p,
      chart_type:       fallback === "area" ? "line" : fallback,
      is_area:          fallback === "area",
      _conversionError: null,
      _autoConvertTo:   null,
    }));
  }, [editOverrides._autoConvertTo, chartData.chart_type, setEditOverrides]);

  const activeError     = editOverrides._conversionError || null;
  const autoConvertTo   = editOverrides._autoConvertTo || null;
  const autoConvertLabel = autoConvertTo
    ? (dropdownOptions.find((o) => o.value === autoConvertTo)?.label || autoConvertTo)
    : null;

  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
        Chart Type
      </p>

      <div className="relative">
        <select
          className={[
            "database-selector-control w-full text-[11px] font-medium",
            activeError
              ? "border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/10"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ minWidth: 0, height: "30px" }}
          value={effectiveType}
          onChange={(e) => handleChange(e.target.value)}
          aria-label="Select chart type"
        >
          {dropdownOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={!option.valid}
              title={!option.valid ? option.reason : undefined}
              style={!option.valid ? { color: "#94a3b8" } : undefined}
            >
              {option.label}
              {!option.valid ? " (incompatible)" : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Error banner — only visible when an incompatible type is somehow selected */}
      {activeError && (
        <ConversionError
          message={activeError}
          onAutoFix={autoConvertTo ? handleAutoFix : null}
          autoFixLabel={autoConvertLabel}
        />
      )}
    </div>
  );
}